import { APP_CONFIG } from '../config/appConfig.js';

export const validateInputs = (inputs) => {
  const errors = [];
  const warnings = [];

  // Check required fields
  if (!inputs.litres || inputs.litres <= 0) {
    errors.push('Monthly diesel litres must be greater than 0');
  }
  if (!inputs.pricePerLitre || inputs.pricePerLitre <= 0) {
    errors.push('Diesel price per litre must be greater than 0');
  }
  if (!inputs.hours || inputs.hours <= 0) {
    errors.push('Generator hours per month must be greater than 0');
  }
  if (!inputs.avgLoad || inputs.avgLoad <= 0) {
    errors.push('Average load must be greater than 0 kW');
  }

  // Check for zero kWh generation
  if (inputs.hours > 0 && inputs.avgLoad > 0) {
    const kWh = inputs.avgLoad * inputs.hours;
    if (kWh === 0) {
      errors.push('We need both hours and kW above zero to estimate kWh');
    }
  }

  // Hours validation
  if (inputs.hours > APP_CONFIG.limits.maxHoursPerMonth) {
    warnings.push(`Hours exceed maximum possible (${APP_CONFIG.limits.maxHoursPerMonth} hours/month). Please check your input.`);
  }

  // Large value warnings
  if (inputs.litres > APP_CONFIG.limits.largeValueThreshold) {
    warnings.push('That is a big number for litres. Double check your inputs.');
  }
  if (inputs.pricePerLitre > 100) {
    warnings.push('That is a very high price per litre. Double check your inputs.');
  }

  return { errors, warnings };
};

export const calculateResults = (inputs) => {
  const { litres, pricePerLitre, hours, avgLoad } = inputs;

  // Basic calculations
  const monthlySpend = litres * pricePerLitre;
  const kWhGenerated = avgLoad * hours;
  const fuelCostPerKwh = monthlySpend / kWhGenerated;

  // All-in estimates
  const allInLow = fuelCostPerKwh * APP_CONFIG.allInMultipliers.low;
  const allInHigh = fuelCostPerKwh * APP_CONFIG.allInMultipliers.high;

  return {
    monthlySpend,
    kWhGenerated,
    fuelCostPerKwh,
    allInLow,
    allInHigh
  };
};

export const formatCurrency = (value, decimals = 2) => {
  return `${APP_CONFIG.currency.symbol}${value.toFixed(decimals)}`;
};

export const formatNumber = (value, decimals = null) => {
  if (decimals === null) {
    // Auto-format based on size
    if (value < 1) return value.toFixed(3);
    if (value < 100) return value.toFixed(2);
    return value.toFixed(0);
  }
  return value.toFixed(decimals);
};

export const generateShareText = (results, showAllIn = false) => {
  const fuelCost = formatCurrency(results.fuelCostPerKwh, 3);
  
  if (showAllIn) {
    const lowCost = formatCurrency(results.allInLow, 3);
    const highCost = formatCurrency(results.allInHigh, 3);
    return `My generator fuel cost is about ${fuelCost} per kWh (fuel only). Estimated all-in range: ${lowCost} to ${highCost} per kWh.`;
  }
  
  return `My generator fuel cost is about ${fuelCost} per kWh (fuel only).`;
};

export const generateCSVData = (inputs, results, timestamp) => {
  const headers = ['Field', 'Value', 'Unit'];
  const rows = [
    headers,
    ['Calculation Date', timestamp, ''],
    ['Monthly Diesel Used', inputs.litres, 'litres'],
    ['Diesel Price per Litre', inputs.pricePerLitre, APP_CONFIG.currency.code],
    ['Generator Hours per Month', inputs.hours, 'hours'],
    ['Average Load', inputs.avgLoad, 'kW'],
    ['Monthly Diesel Spend', results.monthlySpend.toFixed(2), APP_CONFIG.currency.code],
    ['Estimated kWh Generated', results.kWhGenerated.toFixed(2), 'kWh'],
    ['Fuel-only Cost per kWh', results.fuelCostPerKwh.toFixed(3), `${APP_CONFIG.currency.code}/kWh`],
    ['All-in Low Estimate', results.allInLow.toFixed(3), `${APP_CONFIG.currency.code}/kWh`],
    ['All-in High Estimate', results.allInHigh.toFixed(3), `${APP_CONFIG.currency.code}/kWh`]
  ];
  
  return rows.map(row => row.join(',')).join('\n');
};